import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErr, fmtMoney, API } from "../lib/api";
import { LOGO_URL } from "../lib/permissions";
import { DEFAULT_CONTENT, mergeContent } from "../lib/siteContent";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Leaf, Fish, Egg, Sprout, Beef, Shield, Sparkles, Truck, BadgeCheck,
  MapPin, Phone, Mail, MessageCircle, Instagram, Facebook, Menu, X,
} from "lucide-react";
import { toast } from "sonner";

const NAV = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "products", label: "Products" },
  { id: "updates", label: "Updates" },
  { id: "contact", label: "Contact" },
];

const PRODUCE = [
  { icon: Beef, title: "Goats", text: "Healthy, well-fed, and ideal for fresh meat supply." },
  { icon: Beef, title: "Pigs", text: "Premium pork raised in clean, controlled environments." },
  { icon: Fish, title: "Fish", text: "Catfish and tilapia from our modern aquaculture system." },
  { icon: Sparkles, title: "Snails", text: "Large, healthy snails for consumption and breeding." },
  { icon: Egg, title: "Poultry", text: "Eggs and broilers raised with proper nutrition." },
  { icon: Sprout, title: "Crops", text: "Vegetables, fruits, and grains grown seasonally." },
];

const WHY = [
  { icon: Leaf, title: "Ethical & Sustainable", text: "Farming that respects animals, people and the land." },
  { icon: Shield, title: "Clean Facilities", text: "Well-managed, hygienic pens, ponds and units." },
  { icon: BadgeCheck, title: "Transparent Process", text: "Honest, traceable farm-to-table practices." },
  { icon: Sparkles, title: "Fresh & High Quality", text: "Premium produce, harvested at its best." },
  { icon: Truck, title: "Trusted Supply", text: "Relied on by families, restaurants and retailers." },
];

// Icon rotations used to render dynamic CMS-driven produce/why items.
const PRODUCE_ICONS = [Beef, Beef, Fish, Sparkles, Egg, Sprout];
const WHY_ICONS = [Leaf, Shield, BadgeCheck, Sparkles, Truck];

const TESTIMONIALS = [
  { quote: "Modav Farms is my number one source for fresh meat. Always reliable.", by: "Local Customer, Ogun State" },
  { quote: "Their snails and fish are top quality. Highly recommended.", by: "Restaurant Owner, Ijebu Area" },
];

const UPDATES = [
  "New batch of snails available for wholesale",
  "Fresh catfish harvest this week",
  "Poultry unit expansion completed",
  "Community outreach program for local farmers",
];

const CAT_ICON = { Livestock: Beef, Meat: Beef, Fishery: Fish, Poultry: Egg, Processed: Sparkles, Crops: Sprout };

const Section = ({ id, className = "", children }) => (
  <section id={id} className={`scroll-mt-20 px-6 py-20 sm:px-10 lg:px-16 ${className}`}>{children}</section>
);

function ProductCard({ p }) {
  const Icon = CAT_ICON[p.category] || Leaf;
  return (
    <div className="group overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white transition-all duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid="public-product">
      <div className="flex h-40 items-center justify-center bg-gradient-to-br from-[#EAF1EA] to-[#E4EEF6]">
        {p.image_file
          ? <img src={`${API}/public/files/${p.image_file}`} alt={p.name} className="h-full w-full object-cover" />
          : <Icon className="h-12 w-12 text-[#2C5530]/40" />}
      </div>
      <div className="p-4">
        <span className="text-[11px] font-medium uppercase tracking-wider text-[#1565C0]">{p.category}</span>
        <h4 className="font-display text-base font-bold leading-tight text-[#1A1D1A]">{p.name}</h4>
        <p className="mt-2 num font-display text-lg font-extrabold text-[#2C5530]">{fmtMoney(p.price)}<span className="text-xs font-medium text-[#5C665E]">/{p.unit}</span></p>
      </div>
    </div>
  );
}

export function Landing() {
  const [products, setProducts] = useState([]);
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [menu, setMenu] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/public/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/public/content").then(({ data }) => setContent(mergeContent(data))).catch(() => {});
  }, []);

  const featured = products.filter((p) => p.featured).slice(0, 8);
  const shop = featured.length ? featured : products.slice(0, 8);

  const sendContact = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/contact", contact);
      toast.success("Message sent! We'll get back to you shortly.");
      setContact({ name: "", email: "", phone: "", message: "" });
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  const go = (id) => { setMenu(false); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); };

  return (
    <div className="min-h-screen bg-[#F8F9FA] font-sans text-[#1A1D1A]">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[#E2E8F0] bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <button onClick={() => go("home")} className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Modav Farms" className="h-11 w-11 rounded-full" />
            <span className="font-display text-lg font-extrabold tracking-tight">Modav Farms</span>
          </button>
          <nav className="hidden items-center gap-7 md:flex">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => go(n.id)} className="text-sm font-medium text-[#5C665E] transition-colors hover:text-[#2C5530]" data-testid={`landing-nav-${n.id}`}>{n.label}</button>
            ))}
            <Link to="/shop" className="text-sm font-semibold text-[#2C5530] hover:underline" data-testid="header-shop-link">Shop</Link>
            <Link to="/login"><Button className="rounded-full bg-[#2C5530] hover:bg-[#214324]" data-testid="staff-login-btn">Staff Login</Button></Link>
          </nav>
          <button className="md:hidden" onClick={() => setMenu(!menu)} data-testid="landing-menu">{menu ? <X /> : <Menu />}</button>
        </div>
        {menu && (
          <div className="border-t border-[#E2E8F0] bg-white px-6 py-3 md:hidden">
            {NAV.map((n) => <button key={n.id} onClick={() => go(n.id)} className="block w-full py-2 text-left text-sm font-medium text-[#5C665E]">{n.label}</button>)}
            <Link to="/login"><Button className="mt-2 w-full rounded-full bg-[#2C5530]">Staff Login</Button></Link>
          </div>
        )}
      </header>

      {/* Hero */}
      <Section id="home" className="relative overflow-hidden !py-0">
        <div className="absolute inset-0">
          <img src={content.hero.image_file ? `${API}/public/files/${content.hero.image_file}` : "https://images.pexels.com/photos/2382665/pexels-photo-2382665.jpeg"} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#15281B]/95 via-[#1E2B22]/85 to-[#1E2B22]/50" />
        </div>
        <div className="relative mx-auto max-w-7xl px-2 py-28 sm:py-36">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.15em] text-white/90 ring-1 ring-white/20">
            <Leaf className="h-3.5 w-3.5" /> {content.hero.badge}
          </span>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl">
            {content.hero.title1}<br /><span className="text-[#8FCB7F]">{content.hero.title2}</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg">
            {content.hero.subtitle}
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/shop"><Button className="rounded-full bg-[#2C5530] px-7 py-6 text-base hover:bg-[#214324]" data-testid="hero-shop">Shop Products</Button></Link>
            <Button onClick={() => go("about")} variant="outline" className="rounded-full border-white/40 bg-transparent px-7 py-6 text-base text-white hover:bg-white/10" data-testid="hero-learn">Learn About Our Farm</Button>
          </div>
        </div>
      </Section>

      {/* What We Produce */}
      <Section id="produce" className="mx-auto max-w-7xl">
        <div className="mb-12 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#1565C0]">What We Produce</p>
          <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Raised with care, grown with purpose</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {content.produce.map((p, i) => {
            const Icon = PRODUCE_ICONS[i % PRODUCE_ICONS.length];
            return (
              <div key={i} className="rounded-2xl border border-[#E2E8F0] bg-white p-6 transition-all duration-200 hover:border-[#2C5530]/40 hover:shadow-sm">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#EAF1EA] text-[#2C5530]"><Icon className="h-6 w-6" /></div>
                <h3 className="mt-4 font-display text-xl font-bold">{p.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-[#5C665E]">{p.text}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* About */}
      <Section id="about" className="bg-[#1E2B22] text-white">
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#8FCB7F]">Our Story</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{content.about.title}</h2>
            <p className="mt-5 leading-relaxed text-white/75">{content.about.story1}</p>
            <p className="mt-4 leading-relaxed text-white/75">{content.about.story2}</p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {content.about.values.map((v) => (
                <span key={v} className="rounded-full bg-white/10 px-4 py-1.5 text-sm text-white/90 ring-1 ring-white/15">{v}</span>
              ))}
            </div>
          </div>
          {content.about.image_file ? (
            <img src={`${API}/public/files/${content.about.image_file}`} alt="" className="h-72 w-full rounded-2xl object-cover ring-1 ring-white/10 lg:h-96" />
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {content.about.gallery.map((m, i) => (
                <div key={i} className={`rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 ${i % 2 ? "mt-6" : ""}`}>
                  <Sparkles className="h-5 w-5 text-[#8FCB7F]" />
                  <p className="mt-3 text-sm font-medium text-white/90">{m}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* Why Choose */}
      <Section id="why" className="mx-auto max-w-7xl">
        <div className="mb-12 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#1565C0]">Why Choose Modav Farms</p>
          <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Quality you can trust</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {content.why.map((w, i) => {
            const Icon = WHY_ICONS[i % WHY_ICONS.length];
            return (
              <div key={i} className="rounded-2xl border border-[#E2E8F0] bg-white p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E4EEF6] text-[#1565C0]"><Icon className="h-5 w-5" /></div>
                <h3 className="mt-3 font-display text-base font-bold">{w.title}</h3>
                <p className="mt-1 text-sm leading-snug text-[#5C665E]">{w.text}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Products / Shop */}
      <Section id="products" className="bg-[#EFF3EE]">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#1565C0]">Products & Shop</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Featured Products</h2>
            <p className="mt-3 text-sm text-[#5C665E]">Fresh goat meat · Catfish · Snails · Poultry & Eggs · Seasonal crops</p>
          </div>
          {shop.length === 0 ? (
            <p className="text-center text-[#5C665E]">Our catalog is being updated. Please check back soon.</p>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {shop.map((p) => <ProductCard key={p.id} p={p} />)}
            </div>
          )}
          <div className="mt-10 text-center">
            <Link to="/shop"><Button className="rounded-full bg-[#2C5530] px-7 py-6 text-base hover:bg-[#214324]" data-testid="browse-all">Browse All Products & Order</Button></Link>
          </div>
        </div>
      </Section>

      {/* Testimonials */}
      <Section id="testimonials" className="mx-auto max-w-5xl">
        <div className="grid gap-6 sm:grid-cols-2">
          {content.testimonials.map((t, i) => (
            <div key={i} className="rounded-2xl border border-[#E2E8F0] bg-white p-8">
              {t.image_file
                ? <img src={`${API}/public/files/${t.image_file}`} alt="" className="mb-3 h-12 w-12 rounded-full object-cover" />
                : <div className="font-display text-5xl leading-none text-[#2C5530]/20">&ldquo;</div>}
              <p className={`${t.image_file ? "" : "-mt-4"} text-lg font-medium leading-relaxed`}>{t.quote}</p>
              <p className="mt-4 text-sm font-semibold text-[#1565C0]">— {t.by}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Updates / Blog */}
      <Section id="updates" className="bg-[#1565C0]/5">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#1565C0]">Farm Updates & Blog</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Latest from the farm</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {content.updates.map((u, i) => (
              <div key={i} className="flex items-start gap-4 rounded-2xl border border-[#E2E8F0] bg-white p-6">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#EAF1EA] font-display font-bold text-[#2C5530]">{i + 1}</div>
                <div>
                  <p className="font-display font-bold">{u}</p>
                  <p className="mt-1 text-sm text-[#5C665E]">Modav Farms · Ilese, Ogun State</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Contact */}
      <Section id="contact" className="mx-auto max-w-7xl">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#1565C0]">Contact Us</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">We'd love to hear from you</h2>
            <p className="mt-4 leading-relaxed text-[#5C665E]">Whether you want to place an order, partner with us, or visit the farm, our team is ready to help.</p>
            <div className="mt-8 space-y-4">
              <a className="flex items-start gap-3 text-sm" href={`https://maps.google.com/?q=${encodeURIComponent(content.contact.address)}`} target="_blank" rel="noreferrer"><MapPin className="mt-0.5 h-5 w-5 text-[#2C5530]" /><span>{content.contact.address}</span></a>
              <a className="flex items-center gap-3 text-sm" href={`tel:${content.contact.phone}`}><Phone className="h-5 w-5 text-[#2C5530]" />{content.contact.phone}</a>
              <a className="flex items-center gap-3 text-sm" href={`mailto:${content.contact.email}`}><Mail className="h-5 w-5 text-[#2C5530]" />{content.contact.email}</a>
              {content.contact.whatsapp && <a className="flex items-center gap-3 text-sm" href={`https://wa.me/${content.contact.whatsapp}`} target="_blank" rel="noreferrer" data-testid="whatsapp-link"><MessageCircle className="h-5 w-5 text-[#2C5530]" />Chat on WhatsApp</a>}
            </div>
            <div className="mt-6 flex gap-3">
              {content.contact.instagram && <a href={content.contact.instagram} target="_blank" rel="noreferrer" className="flex h-10 w-10 items-center justify-center rounded-full bg-[#EAF1EA] text-[#2C5530] hover:bg-[#2C5530] hover:text-white"><Instagram className="h-5 w-5" /></a>}
              {content.contact.facebook && <a href={content.contact.facebook} target="_blank" rel="noreferrer" className="flex h-10 w-10 items-center justify-center rounded-full bg-[#EAF1EA] text-[#2C5530] hover:bg-[#2C5530] hover:text-white"><Facebook className="h-5 w-5" /></a>}
              {content.contact.tiktok && <a href={content.contact.tiktok} target="_blank" rel="noreferrer" className="flex h-10 w-10 items-center justify-center rounded-full bg-[#EAF1EA] text-[#2C5530] hover:bg-[#2C5530] hover:text-white"><Music2 className="h-5 w-5" /></a>}
            </div>
          </div>
          <form onSubmit={sendContact} className="rounded-2xl border border-[#E2E8F0] bg-white p-7">
            <h3 className="font-display text-lg font-bold">Send a Message</h3>
            <div className="mt-4 space-y-3">
              <Input placeholder="Your name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} required data-testid="contact-name" />
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="contact-email" />
                <Input placeholder="Phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="contact-phone" />
              </div>
              <Textarea placeholder="How can we help?" rows={4} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} required data-testid="contact-message" />
              <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#2C5530] py-6 hover:bg-[#214324]" data-testid="contact-submit">{busy ? "Sending…" : "Send a Message"}</Button>
            </div>
          </form>
        </div>
      </Section>

      {/* Footer */}
      <footer className="bg-[#15281B] px-6 py-12 text-center text-white/70">
        <img src={LOGO_URL} alt="" className="mx-auto h-14 w-14 rounded-full" />
        <p className="mt-4 font-display text-lg font-bold text-white">{content.footer.tagline}</p>
        <p className="mt-1 text-sm">{content.footer.subline}</p>
        <p className="mt-6 text-xs">© 2026 Modav Farms. All rights reserved.</p>
        <Link to="/login" className="mt-3 inline-block text-xs text-[#8FCB7F] hover:underline">Staff Login</Link>
      </footer>
    </div>
  );
}
